import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useAuthStore } from '@avanta/shared/stores/auth';
import type { AuthInfo } from '@avanta/shared/types/auth';
import { isLoginAuthError, LOGIN_ERROR_CODE } from '@avanta/shared/utils/login-errors';

type Authenticate = (username: string, password: string, magicToken?: string) => Promise<AuthInfo>;
type Login = (username: string, password: string, magicToken?: string) => Promise<void>;

export interface UseLoginOptions {
  authenticate: Authenticate;
  onSuccess: (authInfo: AuthInfo) => void | Promise<void>;
}

export interface UseLoginReturn {
  login: Login;
  isLoading: boolean;
}

const getLoginErrorMessage = (
  error: unknown,
  t: (key: string, options?: { ns: string }) => string
): string => {
  if (isLoginAuthError(error) && error.code === LOGIN_ERROR_CODE.RECOVER_PASSWORD) {
    return t('Login.errors.recoverPassword', { ns: LOCIZE_NS.AUTH });
  }

  if (error instanceof Error) {
    return t('Login.error', { ns: LOCIZE_NS.AUTH });
  }

  return t('Common.error');
};

export const useLogin = ({ authenticate, onSuccess }: UseLoginOptions): UseLoginReturn => {
  const { t } = useTranslation(LOCIZE_NS.COMMON);
  const setAuthInfo = useAuthStore(state => state.setAuthInfo);
  const [isLoading, setIsLoading] = useState(false);

  const login: Login = async (username, password, magicToken) => {
    setIsLoading(true);
    try {
      const authInfo = await authenticate(username, password, magicToken);
      setAuthInfo(authInfo);
      await onSuccess(authInfo);
    } catch (error) {
      toast.error(getLoginErrorMessage(error, t));
    } finally {
      setIsLoading(false);
    }
  };

  return { login, isLoading };
};
